'use client';
import React from 'react';

import { cn } from '@/lib/utils';

import { Text } from '@/decodo/components/atoms/Text';
import {
  VideoPopup,
  VideoPopupProps,
} from '@/decodo/components/atoms/videoPopup';
import { AnimatedBackground } from '@/decodo/features/HomepageHero/components/AnimatedBackground';
import { AnimatedHeading } from '@/decodo/features/HomepageHero/components/AnimatedHeading';
import { ReputationBadges } from '@/decodo/features/HomepageHero/components/ReputationBadges';
import { getWrapperMaxWidth } from '@/features/utils/utils';
import { useTranslation } from '@/lib/i18n/i18n.client';
import AnimatedEl from '@/lib/motion';
import IconShield from '~/svg/IconShield.svg';

export type HeroProps = {
  title: string;
  description: React.ReactNode;
  image: React.ReactNode;
  links: React.ReactNode;
  videoProps?: VideoPopupProps['videoProps'];
  showMoneyBack?: boolean;
  areReviewsVisible?: boolean;
  showFancyBackground?: boolean;
};

export function Hero({
  image,
  links,
  title,
  description,
  videoProps,
  showMoneyBack,
  areReviewsVisible = true,
  showFancyBackground = true,
}: HeroProps) {
  const { texts } = useTranslation();

  const fadeInVariants = {
    initial: { opacity: 0, y: 8 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 1 },
    },
  };

  const scaleInVariants = {
    initial: { opacity: 0, x: 24 },
    animate: {
      opacity: 1,
      x: 0,
      transition: { duration: 1, ease: 'easeOut' },
    },
  };

  return (
    <AnimatedEl
      el='div'
      initial='initial'
      animate='animate'
      className='md:py-15 md:px-15 relative flex justify-center bg-cover bg-center p-5 py-8'
    >
      {showFancyBackground && (
        <div className='absolute inset-0 hidden items-end justify-center overflow-hidden md:flex'>
          <AnimatedBackground />
        </div>
      )}

      <div
        className={cn(
          getWrapperMaxWidth('mid'),
          'static z-10 flex w-full flex-row items-center gap-6 md:px-5 md:py-8',
          showFancyBackground && 'md:bg-gray-25/50 md:backdrop-blur-[4px]'
        )}
      >
        <div className='flex w-full flex-1 flex-col'>
          {areReviewsVisible && (
            <ReputationBadges classNames={{ root: 'mb-6' }} delayFactor={0.1} />
          )}
          <AnimatedHeading
            level='h1'
            separator=''
            classNames={{
              root: 'mb-6 overflow-hidden',
            }}
          >
            {title}
          </AnimatedHeading>
          <AnimatedEl
            el='div'
            className='mb-9 text-gray-950'
            variants={fadeInVariants}
          >
            {description}
          </AnimatedEl>
          {links && (
            <AnimatedEl
              el='div'
              className={cn(
                'flex flex-wrap gap-6',
                showMoneyBack ? 'mb-4' : 'mb-9'
              )}
              variants={fadeInVariants}
              data-tracker-component='buttons'
            >
              {links}
            </AnimatedEl>
          )}
          {showMoneyBack && (
            <AnimatedEl
              el='div'
              variants={fadeInVariants}
              className={cn('flex items-center gap-2')}
            >
              <IconShield className='text-success-500 h-4 w-4' />
              <Text variant='body-s' color='gray-500'>
                {texts.pricing_cards.money_back.money_back_option}
              </Text>
            </AnimatedEl>
          )}
        </div>
        <AnimatedEl
          el='div'
          variants={scaleInVariants}
          className='relative hidden h-full w-full flex-1 items-center justify-center md:flex'
        >
          {image}
          {videoProps && (
            <VideoPopup
              classNames={{
                targetWrapper:
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
              }}
              videoProps={videoProps}
            />
          )}
        </AnimatedEl>
      </div>
    </AnimatedEl>
  );
}
